/* eslint-disable @typescript-eslint/no-use-before-define */
// Abstract base ↔ concrete subclass references below are intentional and safe
// under class hoisting — every reference site runs after both classes are fully
// initialized.
import { case as case_, union, void as voidType } from "@stellar/js-xdr";
import { XdrError, type XdrType } from "@stellar/js-xdr";
import { XdrValue } from "../values/xdr-value.js";
import { CreateAccountResultCode } from "./create-account-result-code.js";

export type CreateAccountResultWire =
  | { code: 0 }
  | { code: -1 }
  | { code: -2 }
  | { code: -3 }
  | { code: -4 };

export type CreateAccountResultVariantName =
  | "createAccountSuccess"
  | "createAccountMalformed"
  | "createAccountUnderfunded"
  | "createAccountLowReserve"
  | "createAccountAlreadyExist";

/**
 * ```xdr
 * union CreateAccountResult switch (CreateAccountResultCode code)
 * {
 * case CREATE_ACCOUNT_SUCCESS:
 *     void;
 * case CREATE_ACCOUNT_MALFORMED:
 * case CREATE_ACCOUNT_UNDERFUNDED:
 * case CREATE_ACCOUNT_LOW_RESERVE:
 * case CREATE_ACCOUNT_ALREADY_EXIST:
 *     void;
 * };
 * ```
 */
abstract class CreateAccountResultBase extends XdrValue {
  abstract readonly type: CreateAccountResultVariantName;

  constructor() {
    super();
    // `new.target`, not an unconditional throw: every arm subclass reaches
    // this constructor through `super()`, void arms via an implicit one
    if (new.target === CreateAccountResultBase) {
      throw new TypeError(
        "new xdr.CreateAccountResult(...) is not supported: XDR unions are built from " +
          "per-variant factories. Call xdr.CreateAccountResult.createAccountSuccess() " +
          "(or another arm factory) instead.",
      );
    }
  }

  static readonly schema: XdrType<CreateAccountResultWire> = union(
    "CreateAccountResult",
    {
      switchOn: CreateAccountResultCode.schema,
      cases: [
        case_("createAccountSuccess", 0, voidType()),
        case_("createAccountMalformed", -1, voidType()),
        case_("createAccountUnderfunded", -2, voidType()),
        case_("createAccountLowReserve", -3, voidType()),
        case_("createAccountAlreadyExist", -4, voidType()),
      ],
      switchKey: "code",
    },
  );

  static createAccountSuccess(): CreateAccountResultSuccess {
    return new CreateAccountResultSuccess();
  }

  static createAccountMalformed(): CreateAccountResultMalformed {
    return new CreateAccountResultMalformed();
  }

  static createAccountUnderfunded(): CreateAccountResultUnderfunded {
    return new CreateAccountResultUnderfunded();
  }

  static createAccountLowReserve(): CreateAccountResultLowReserve {
    return new CreateAccountResultLowReserve();
  }

  static createAccountAlreadyExist(): CreateAccountResultAlreadyExist {
    return new CreateAccountResultAlreadyExist();
  }

  static fromXdrObject(wire: CreateAccountResultWire): CreateAccountResult {
    switch (wire.code) {
      case 0:
        return new CreateAccountResultSuccess();
      case -1:
        return new CreateAccountResultMalformed();
      case -2:
        return new CreateAccountResultUnderfunded();
      case -3:
        return new CreateAccountResultLowReserve();
      case -4:
        return new CreateAccountResultAlreadyExist();
    }
    // unreachable for a well-typed wire object; a hand-built one can still
    // carry an out-of-range discriminant
    throw new XdrError(
      `CreateAccountResult: unknown code ${(wire as { code: unknown }).code}`,
    );
  }

  /**
   * Type guard narrowing an unknown value to a concrete CreateAccountResult variant.
   * Use this instead of `instanceof CreateAccountResult`: the exported `CreateAccountResult` value
   * is the abstract base, so `instanceof` narrows to the base (not the
   * variant union) and forces a cast. `CreateAccountResult.is(x)` narrows to the union.
   */
  static is(value: unknown): value is CreateAccountResult {
    return value instanceof CreateAccountResultBase;
  }

  abstract toXdrObject(): CreateAccountResultWire;
}

export class CreateAccountResultSuccess extends CreateAccountResultBase {
  readonly type = "createAccountSuccess" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<CreateAccountResultWire, { code: 0 }> {
    return { code: 0 };
  }
}

export class CreateAccountResultMalformed extends CreateAccountResultBase {
  readonly type = "createAccountMalformed" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<CreateAccountResultWire, { code: -1 }> {
    return { code: -1 };
  }
}

export class CreateAccountResultUnderfunded extends CreateAccountResultBase {
  readonly type = "createAccountUnderfunded" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<CreateAccountResultWire, { code: -2 }> {
    return { code: -2 };
  }
}

export class CreateAccountResultLowReserve extends CreateAccountResultBase {
  readonly type = "createAccountLowReserve" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<CreateAccountResultWire, { code: -3 }> {
    return { code: -3 };
  }
}

export class CreateAccountResultAlreadyExist extends CreateAccountResultBase {
  readonly type = "createAccountAlreadyExist" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<CreateAccountResultWire, { code: -4 }> {
    return { code: -4 };
  }
}

export type CreateAccountResult =
  | CreateAccountResultSuccess
  | CreateAccountResultMalformed
  | CreateAccountResultUnderfunded
  | CreateAccountResultLowReserve
  | CreateAccountResultAlreadyExist;
export const CreateAccountResult = CreateAccountResultBase;
